import {Checkout, ModelContext} from '../../../../types/checkoutApp.types';
import {mapNullToUndefined} from '../../../utils/mapperHelper.utils';
import {SettingsModel} from './SettingsModel';
import {CustomerInfoModel} from './CustomerInfo.model';
import {BusinessInfoModel} from './BusinessInfo.model';
import {SourceModel} from './Source.model';
import {PaymentInfoModel} from './PaymentInfo.model';
import {DeliveryInfoModel} from './DeliveryInfo.model';
import {CheckoutErrorCode, ErrorType} from '../../../utils/errors';
import {CheckoutErrorModel} from '../CheckoutError.model';
import {LineItemModel} from '../LineItems/LineItem.model';
import {SPECS} from '../../../../common/constants';
import {ItemTypePreset} from '../LineItems/ItemType.model';
import {CouponModel} from './Coupon.model';
import {PaymentPolicyModel} from '../../common/PaymentPolicy.model';
import {CustomFieldModel} from './CustomField.model';
import {PriceModel} from '../Price.model';
import {TaxInfoModel} from './TaxInfo.model';

export class CartModel {
  public id: string;
  public purchaseFlowId: string;
  public lineItems: LineItemModel[];
  public updatedDate: string;
  /**
   * @deprecated
   */
  public cartId?: string;
  public settings?: SettingsModel;
  public customerInfo: CustomerInfoModel;
  public businessInfo: BusinessInfoModel;
  public source: SourceModel;
  public paymentInfo: PaymentInfoModel;
  public deliveryInfo: DeliveryInfoModel;
  public taxInfo: TaxInfoModel;
  public subtotal: PriceModel;
  public extendedFields?: {namespaces?: any};
  public customField?: CustomFieldModel;

  public coupon?: CouponModel;
  public noItemsError?: CheckoutErrorModel;
  public lineItemPolicies: Record<string, string[]>;
  public hasDigitalItems: boolean;
  public hasGiftCardItems: boolean;
  public numberOfItems: number;
  public hasShippableItems: boolean;
  public paymentPolicies: PaymentPolicyModel[];
  public isCardTokenizationCheckout: boolean;
  public hasOutOfStockLineItems: boolean;
  public hasMembersOnlyItems: boolean;
  public hasItemsWithLongPrice: boolean;
  public hasSubscriptionItems: boolean;
  public itemTypes: Set<ItemTypePreset>;
  public hasRecurringPaymentSubscription: boolean;

  constructor(params: CartModel) {
    this.id = params.id;
    this.purchaseFlowId = params.purchaseFlowId;
    this.lineItems = params.lineItems;
    this.updatedDate = params.updatedDate;
    this.cartId = params.cartId;
    this.settings = params.settings;
    this.customerInfo = params.customerInfo;
    this.businessInfo = params.businessInfo;
    this.source = params.source;
    this.paymentInfo = params.paymentInfo;
    this.deliveryInfo = params.deliveryInfo;
    this.taxInfo = params.taxInfo;
    this.subtotal = params.subtotal;
    this.extendedFields = params.extendedFields;
    this.customField = params.customField;
    this.coupon = params.coupon;
    this.lineItemPolicies = params.lineItemPolicies;
    this.hasDigitalItems = params.hasDigitalItems;
    this.hasGiftCardItems = params.hasGiftCardItems;
    this.numberOfItems = params.numberOfItems;
    this.hasShippableItems = params.hasShippableItems;
    this.paymentPolicies = params.paymentPolicies;
    this.isCardTokenizationCheckout = params.isCardTokenizationCheckout;
    this.hasOutOfStockLineItems = params.hasOutOfStockLineItems;
    this.hasMembersOnlyItems = params.hasMembersOnlyItems;
    this.hasItemsWithLongPrice = params.hasItemsWithLongPrice;
    this.hasSubscriptionItems = params.hasSubscriptionItems;
    this.itemTypes = params.itemTypes;
    this.hasRecurringPaymentSubscription = params.hasRecurringPaymentSubscription;
    this.noItemsError = params.noItemsError;
  }

  public static fromCheckoutV1(checkout: Checkout, modelContext: ModelContext): CartModel {
    const modeledLineItems = (checkout.lineItems ?? []).map((lineItem) =>
      LineItemModel.fromCheckoutV1(
        lineItem!,
        modelContext.flowAPI.experiments.enabled(SPECS.DontUseLineItemEnricherInCartAndCheckout)
          ? undefined
          : checkout.externalEnrichedLineItems?.enrichedLineItems,
        checkout.membershipOptions!,
        modelContext
      )
    );
    const hasSubscriptionItems = modeledLineItems.some((lineItem) => lineItem.attributes.subscriptionInfo);
    const noItemsError = getNoItemsError(modeledLineItems);

    const itemTypes = new Set(modeledLineItems.map(({attributes}) => attributes.itemType.preset!).filter(Boolean));

    return new CartModel({
      id: checkout.id!,
      purchaseFlowId: checkout.purchaseFlowId!,
      lineItems: modeledLineItems,
      updatedDate: checkout.updatedDate!,
      cartId: mapNullToUndefined(checkout.cartId),
      settings: SettingsModel.fromCheckoutV1(checkout.customSettings),
      customerInfo: CustomerInfoModel.fromCheckoutV1(checkout.buyerLanguage!, checkout.buyerInfo),
      businessInfo: BusinessInfoModel.fromCheckoutV1(checkout.siteLanguage!),
      source: SourceModel.fromCheckoutV1(checkout.channelType),
      paymentInfo: PaymentInfoModel.fromCheckoutV1({
        currencyCode: checkout.paymentCurrency,
        giftCard: checkout.giftCard,
        billingInfo: checkout.billingInfo,
      }),
      taxInfo: TaxInfoModel.fromCheckoutV1(
        modelContext.flowAPI.experiments.enabled(SPECS.UseTaxIncludedFromCheckout)
          ? checkout.taxIncludedInPrice ?? false
          : undefined
      ),
      deliveryInfo: DeliveryInfoModel.fromCheckoutV1(
        checkout.shippingInfo?.shippingDestination,
        checkout.shippingInfo?.selectedCarrierServiceOption
      ),
      subtotal: PriceModel.fromCheckoutV1(
        modelContext.flowAPI.experiments.enabled(SPECS.ShowPriceSummaryByTaxIncluded) && modelContext.pricesIncludeTax
          ? checkout.lineItemsSubtotal
          : checkout.priceSummary?.subtotal,
        modelContext
      ),
      extendedFields: mapNullToUndefined(checkout.extendedFields),
      customField: CustomFieldModel.fromCheckoutV1(checkout.customFields?.[0]),
      lineItemPolicies: groupItemPolicies(modeledLineItems),
      hasDigitalItems: modeledLineItems.some(
        (lineItem) => lineItem.attributes.itemType.preset === ItemTypePreset.DIGITAL
      ),
      hasGiftCardItems: itemTypes.has(ItemTypePreset.GIFT_CARD),
      numberOfItems: modeledLineItems.reduce(
        (acc: number, {quantityInfo}: LineItemModel) => acc + quantityInfo.quantity,
        0
      ),
      hasShippableItems: modeledLineItems.some((lineItem) => lineItem.attributes.physicalProperties.shippable),
      paymentPolicies: modeledLineItems
        .filter((lineItem) => lineItem.paymentConfig.consentRequiredPaymentPolicy)
        .map((lineItem) =>
          PaymentPolicyModel.fromCheckoutV1(lineItem.name, lineItem.paymentConfig.consentRequiredPaymentPolicy)
        ),
      isCardTokenizationCheckout: modeledLineItems.some((lineItem) => lineItem.paymentConfig.savePaymentMethod),
      hasOutOfStockLineItems: modeledLineItems.some(({quantityInfo}) => quantityInfo.quantity === 0),
      hasMembersOnlyItems: modeledLineItems.some((lineItem) => lineItem.attributes.membersOnly),
      hasItemsWithLongPrice: modeledLineItems.some((lineItem) => lineItem.pricing.breakdown?.isLongPrice),
      hasSubscriptionItems,
      itemTypes,
      hasRecurringPaymentSubscription: modelContext.flowAPI.experiments.enabled(SPECS.FixAllowRecurringPaymentOnlyFlag)
        ? modeledLineItems.some(
            (lineItem) =>
              lineItem.attributes.subscriptionInfo &&
              lineItem.attributes.subscriptionInfo.subscriptionSettings.billingCycles !== 1
          )
        : hasSubscriptionItems,
      noItemsError,
      coupon: CouponModel.fromCheckoutV1(checkout.appliedDiscounts ?? []),
    });
  }
}

function groupItemPolicies(lineItems: LineItemModel[]): Record<string, string[]> {
  return lineItems.reduce((acc: Record<string, string[]>, lineItem) => {
    lineItem.attributes.policies?.forEach((policy) => {
      if (!acc[policy.title]) {
        acc[policy.title] = [];
      }
      /* istanbul ignore next */
      if (policy.content) {
        acc[policy.title].push(policy.content);
      }
    });
    return acc;
  }, {});
}

function getNoItemsError(modeledLineItems: LineItemModel[]): CheckoutErrorModel | undefined {
  const isCheckoutEmpty = modeledLineItems.length === 0;
  const areAllItemsOutOfStock = modeledLineItems.every((lineItem) => lineItem.quantityInfo.quantity === 0);
  if (isCheckoutEmpty) {
    return new CheckoutErrorModel({
      code: CheckoutErrorCode.EMPTY_CHECKOUT,
      type: ErrorType.GENERAL,
    });
  } else if (areAllItemsOutOfStock) {
    return new CheckoutErrorModel({
      code: CheckoutErrorCode.FULL_OUT_OF_STOCK,
      type: ErrorType.GENERAL,
    });
  }
  return undefined;
}
